/* =====================================================================
   assets/soi-buybox.js · Spirit of India · M1 (05 §7.4, 02 §3.7, 00 §6.1 / §7.6)
   <soi-buy>     single-product purchase block: quantity 1 to 10, size pills, ATC (2026-10-14: no bundle offers)
   <soi-setbox>  combo product buy box (quantity stepper, ATC)
   <soi-gallery> 9-slide gallery: swipe, thumbs, arrows, counter, keyboard, slide targets
   Globals: window.soiPrice (shared maths + formatting), science modal openers, buy-box focus links.
   Events: dispatches `soi:buybox:change` (detail = summary for the sticky bar).
   Progressive enhancement: without this file the form posts the selected variant and quantity.
   No frameworks, passive listeners, no layout reads in scroll handlers.
   ===================================================================== */
(() => {
  'use strict';
  if (customElements.get('soi-buy')) return;

  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const NBSP = ' ';
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const fill = (tpl, map) => Object.keys(map).reduce((s, k) => s.split(`[${k}]`).join(map[k]), tpl || '');

  /* ---------------------------------------------------------------- money and maths (00 §6.1, 05 C06) */
  const money = (cents) => {
    const c = Math.round(Number(cents) || 0);
    const neg = c < 0;
    const abs = Math.abs(c);
    const eur = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const ct = String(abs % 100).padStart(2, '0');
    return `${neg ? '−' : ''}${eur},${ct}${NBSP}€`;
  };
  // half-up integer division
  const divRound = (num, den) => (den > 0 ? Math.floor((2 * num + den) / (2 * den)) : 0);
  window.soiPrice = { money, divRound };


  /* ---------------------------------------------------------------- <soi-setbox> (02 §7.7 G) */
  class SoiSetbox extends HTMLElement {
    connectedCallback() {
      this.qty = this.querySelector('[data-setbox-qty]');
      this.atc = this.querySelector('[data-bb-atc]');
      this.err = this.querySelector('[data-bb-error]');
      this.price = Number(this.dataset.price) || 0;
      this.anchor = Number(this.dataset.anchor) || 0;
      this.ml = Number(this.dataset.ml) || 0;
      this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => this.setQty(Number(this.qty.value) + Number(b.dataset.step))));
      this.qty && this.qty.addEventListener('change', () => this.setQty(Number(this.qty.value)));
      const form = this.querySelector('[data-soi-bb-form]');
      form && form.addEventListener('submit', (e) => { e.preventDefault(); this.add('main'); });
      this.setQty(1);
    }
    setQty(q) {
      const v = Math.max(1, Math.min(10, Math.round(q) || 1));
      this.qty.value = v;
      this.querySelector('[data-step="-1"]').disabled = v <= 1;
      this.querySelector('[data-step="1"]').disabled = v >= 10;
      const pay = this.price * v; const anchor = this.anchor * v; const ml = this.ml * v;
      const line1 = this.atc && this.atc.querySelector('[data-bb-atc-line1] .soi-price');
      if (line1) {
        line1.dataset.price = pay;
        line1.querySelector('.soi-price__now').textContent = money(pay);
        const a = line1.querySelector('.soi-price__anchor');
        if (a) a.lastChild.textContent = money(anchor);
        const u = line1.querySelector('[data-soi="unit-price"]');
        if (u && ml) u.textContent = u.textContent.replace(/[\d.]+,\d{2}\s€/, money(divRound(pay * 100, ml)));
      }
      this.summary = { line1: this.atc ? this.atc.querySelector('[data-bb-atc-line1]').innerHTML : '', disabled: this.atc ? this.atc.disabled : false, infoS: null, infoL: null };
      this.dispatchEvent(new CustomEvent('soi:buybox:change', { bubbles: true, detail: this.summary }));
    }
    async add(origin, btnOverride) {
      const btn = btnOverride || this.atc;
      if (!btn || btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
      const src = origin === 'sticky' ? `${this.dataset.source}:sticky` : this.dataset.source;
      // 2026-10-14: no bundle offers, so the only line property is the source
      const props = { _soi_source: src };
      const items = [{ id: Number(this.dataset.variant), quantity: Number(this.qty.value) || 1, properties: props }];
      const line1 = btn.querySelector('.soi-btn__line1');
      const before = line1 ? line1.innerHTML : '';
      btn.style.minInlineSize = `${btn.offsetWidth}px`;
      btn.setAttribute('aria-busy', 'true');
      if (line1) line1.innerHTML = `<span class="soi-btn__label">${esc((window.soiStrings || {}).loading || '')}</span>`;
      try { await window.soiUI.addToCart(items, {}); } catch (e) {
        if (this.err) { this.err.textContent = (window.soiStrings || {}).atcError || ''; this.err.hidden = false; }
      } finally {
        btn.removeAttribute('aria-busy'); btn.style.minInlineSize = '';
        if (line1) line1.innerHTML = before;
      }
    }
  }
  customElements.define('soi-setbox', SoiSetbox);

  /* ---------------------------------------------------------------- <soi-buy> single-product purchase block (2026-10-14)
     price + Grundpreis, quantity 1 to 10, size pills where a real 30 ml variant exists; ATC total = qty × price.
     Adds qty × the selected variant with only _soi_source (no bundle, tier or gift properties). */
  class SoiBuy extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.qty = this.querySelector('[data-buy-qty]');
      this.atc = this.querySelector('[data-bb-atc]');
      this.err = this.querySelector('[data-bb-error]');
      this.idInput = this.querySelector('[data-buy-id]');
      this.variant = Number(this.dataset.variant) || 0;
      this.price = Number(this.dataset.price) || 0;
      this.ml = Number(this.dataset.ml) || 0;
      this.available = !(this.atc && this.atc.disabled);
      this.querySelectorAll('[data-step]').forEach((b) => b.addEventListener('click', () => this.setQty(Number(this.qty.value) + Number(b.dataset.step))));
      if (this.qty) this.qty.addEventListener('change', () => this.setQty(Number(this.qty.value)));
      this.querySelectorAll('.soi-size__pill').forEach((pill) => {
        const input = pill.querySelector('input');
        if (input) input.addEventListener('change', () => { if (input.checked) this.setSize(pill); });
      });
      const form = this.querySelector('[data-soi-bb-form]');
      if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this.add('main'); });
      this.setQty(Number(this.qty && this.qty.value) || 1);
    }
    setSize(pill) {
      this.variant = Number(pill.dataset.variant) || this.variant;
      this.price = Number(pill.dataset.price) || this.price;
      this.ml = Number(pill.dataset.ml) || this.ml;
      this.available = pill.dataset.available !== 'false';
      if (this.idInput) this.idInput.value = this.variant;
      const now = this.querySelector('[data-buy-now]');
      if (now) now.textContent = money(this.price);
      const unit = this.querySelector('[data-buy-unit]');
      if (unit && this.ml) unit.textContent = unit.textContent.replace(/[\d.]+,\d{2}\s€/, money(divRound(this.price * 100, this.ml)));
      if (this.atc) { this.atc.disabled = !this.available; this.atc.toggleAttribute('aria-disabled', !this.available); }
      this.setQty(Number(this.qty.value) || 1);
    }
    setQty(q) {
      if (!this.qty) return;
      const v = Math.max(1, Math.min(10, Math.round(q) || 1));
      this.qty.value = v;
      const minus = this.querySelector('[data-step="-1"]');
      const plus = this.querySelector('[data-step="1"]');
      if (minus) minus.disabled = v <= 1;
      if (plus) plus.disabled = v >= 10;
      const pay = this.price * v;
      const ml = this.ml * v;
      const line1 = this.atc && this.atc.querySelector('[data-bb-atc-line1] .soi-price');
      if (line1) {
        line1.dataset.price = pay;
        const n = line1.querySelector('.soi-price__now');
        if (n) n.textContent = money(pay);
        const u = line1.querySelector('[data-soi="unit-price"]');
        if (u && ml) u.textContent = u.textContent.replace(/[\d.]+,\d{2}\s€/, money(divRound(pay * 100, ml)));
      }
      const gp = this.ml ? money(divRound(this.price * 100, this.ml)) : '';
      const info = gp ? `${gp}/100${NBSP}ml` : null;
      this.summary = {
        line1: this.atc ? this.atc.querySelector('[data-bb-atc-line1]').innerHTML : '',
        disabled: this.atc ? this.atc.disabled : false,
        infoS: info, infoL: info, infoM: info, tier: 1, purchase: 'once',
      };
      this.dispatchEvent(new CustomEvent('soi:buybox:change', { bubbles: true, detail: this.summary }));
    }
    async add(origin, btnOverride) {
      const btn = btnOverride || this.atc;
      if (!btn || !this.available || btn.getAttribute('aria-busy') === 'true') return;
      const src = origin === 'sticky' ? `${this.dataset.source}:sticky` : this.dataset.source;
      const items = [{ id: this.variant, quantity: Number(this.qty.value) || 1, properties: { _soi_source: src } }];
      const line1 = btn.querySelector('.soi-btn__line1');
      const before = line1 ? line1.innerHTML : '';
      btn.style.minInlineSize = `${btn.offsetWidth}px`;
      if (line1) { line1.style.minInlineSize = `${line1.offsetWidth}px`; line1.style.minBlockSize = `${line1.offsetHeight}px`; }
      btn.setAttribute('aria-busy', 'true');
      if (line1) line1.innerHTML = `<span class="soi-btn__label">${esc((window.soiStrings || {}).loading || '')}</span>`;
      if (this.err) this.err.hidden = true;
      this.dispatchEvent(new CustomEvent('soi:buybox:busy', { bubbles: true, detail: { busy: true } }));
      try {
        await window.soiUI.addToCart(items, { name: this.dataset.name });
      } catch (e) {
        if (this.err) { this.err.textContent = (window.soiStrings || {}).atcError || ''; this.err.hidden = false; }
        if (origin === 'sticky' && window.soiToast) window.soiToast((window.soiStrings || {}).atcError || '', { type: 'error' });
      } finally {
        btn.removeAttribute('aria-busy');
        btn.style.minInlineSize = '';
        if (line1) { line1.innerHTML = before; line1.style.minInlineSize = ''; line1.style.minBlockSize = ''; }
        this.dispatchEvent(new CustomEvent('soi:buybox:busy', { bubbles: true, detail: { busy: false } }));
      }
    }
  }
  customElements.define('soi-buy', SoiBuy);

  /* ---------------------------------------------------------------- <soi-gallery> (02 §3.1, 05 C17) */
  class SoiGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gal-track]');
      if (!this.track) return;
      this.slides = [...this.track.querySelectorAll('[data-gal-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gal-thumb]')];
      this.prev = this.querySelector('[data-gal-prev]');
      this.next = this.querySelector('[data-gal-next]');
      this.cur = this.querySelector('[data-gal-current]');
      this.index = 0;
      // R3-G-23: deferred slides (3+) hydrate on the first sign of use, before any of the handlers below run
      if (this.querySelector('template[data-gal-defer]')) {
        const wake = () => this.hydrate();
        ['pointerdown', 'touchstart', 'focusin', 'keydown'].forEach((t) => this.addEventListener(t, wake, { once: true, passive: true, capture: true }));
        this.track.addEventListener('scroll', wake, { once: true, passive: true });
      }
      this.prev && this.prev.addEventListener('click', () => this.go(this.index - 1));
      this.next && this.next.addEventListener('click', () => this.go(this.index + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.galThumb))));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      });
      this.track.addEventListener('click', (e) => {
        if (e.target.closest('button, a')) return;
        const slide = e.target.closest('[data-gal-slide]');
        if (!slide) return;
        const target = slide.dataset.galTarget;
        if (!target) { this.openLightbox(slide); return; }
        if (target === 'science') {
          const btn = slide.querySelector('[data-soi-science-open]');
          btn && btn.click();
        } else {
          const el = document.querySelector(target);
          el && el.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' });
        }
      });
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          entries.forEach((en) => { if (en.isIntersecting && en.intersectionRatio >= 0.6) this.setIndex(this.slides.indexOf(en.target)); });
        }, { root: this.track, threshold: [0.6] });
        this.slides.forEach((s) => this.io.observe(s));
      }
      this.setIndex(0);
    }
    disconnectedCallback() { if (this.io) this.io.disconnect(); }
    /* ----- lightbox for photo slides (02 §3.1): same order, arrows and ←/→, ESC, focus return, pinch zoom on S */
    lightbox() {
      if (this.lb) return this.lb;
      const label = (sel) => { const b = this.querySelector(sel); return b ? b.getAttribute('aria-label') : ''; };
      const icon = (d) => `<svg width="22" height="22" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true" focusable="false"><path d="${d}"/></svg>`;
      const dlg = document.createElement('dialog');
      dlg.className = 'soi-dialog soi-lightbox';
      dlg.setAttribute('aria-label', this.track.getAttribute('aria-label') || '');
      dlg.innerHTML = `<div class="soi-lightbox__bar"><span class="soi-lightbox__count" aria-live="polite" data-lb-count></span>`
        + `<button type="button" class="soi-dialog__close soi-lightbox__close" data-soi-dialog-close aria-label="${esc((document.querySelector('.soi-dialog__close[aria-label]') || { getAttribute: () => 'Schließen' }).getAttribute('aria-label'))}">${icon('M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z')}</button></div>`
        + '<div class="soi-lightbox__stage" data-lb-stage></div>'
        + `<button type="button" class="soi-cc__arrow soi-lightbox__prev" data-lb-prev aria-label="${esc(label('[data-gal-prev]'))}">${icon('M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z')}</button>`
        + `<button type="button" class="soi-cc__arrow soi-lightbox__next" data-lb-next aria-label="${esc(label('[data-gal-next]'))}">${icon('M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z')}</button>`;
      document.body.append(dlg);
      dlg.querySelector('[data-lb-prev]').addEventListener('click', () => this.lbGo(this.lbIndex - 1));
      dlg.querySelector('[data-lb-next]').addEventListener('click', () => this.lbGo(this.lbIndex + 1));
      dlg.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.lbGo(this.lbIndex + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.lbGo(this.lbIndex - 1); }
      });
      dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.matches('[data-lb-stage]')) dlg.close(); });
      this.lb = dlg;
      return dlg;
    }
    lbGo(i) {
      const photos = this.photos;
      if (!photos || !photos.length) return;
      this.lbIndex = Math.max(0, Math.min(photos.length - 1, i));
      const slide = photos[this.lbIndex];
      const stage = this.lb.querySelector('[data-lb-stage]');
      const media = slide.querySelector('.soi-gal__img');
      const clone = media ? media.cloneNode(true) : document.createElement('div');
      clone.classList.add('soi-lightbox__media');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
      stage.replaceChildren(clone);
      const n = this.slides.indexOf(slide);
      this.lb.querySelector('[data-lb-count]').textContent = slide.getAttribute('aria-label') || `${n + 1} / ${this.slides.length}`;
      this.lb.querySelector('[data-lb-prev]').disabled = this.lbIndex <= 0;
      this.lb.querySelector('[data-lb-next]').disabled = this.lbIndex >= photos.length - 1;
      this.go(n);
    }
    openLightbox(slide) {
      this.photos = this.slides.filter((s) => !s.dataset.galTarget);
      const i = this.photos.indexOf(slide);
      if (i < 0) return;
      const dlg = this.lightbox();
      this.lbGo(i);
      if (window.soiUI) window.soiUI.openDialog(dlg, slide.closest('[data-gal-track]'));
      else dlg.showModal();
    }
    hydrate() {
      if (this.hydrated) return;
      this.hydrated = true;
      this.querySelectorAll('template[data-gal-defer]').forEach((t) => t.replaceWith(t.content.cloneNode(true)));
      this.dispatchEvent(new CustomEvent('soi:gallery:hydrated', { bubbles: true }));
    }
    go(i) {
      this.hydrate();
      const n = Math.max(0, Math.min(this.slides.length - 1, i));
      const s = this.slides[n];
      this.track.scrollTo({ left: s.offsetLeft - this.slides[0].offsetLeft, behavior: reduce() ? 'auto' : 'smooth' });
      this.setIndex(n);
    }
    setIndex(i) {
      if (i < 0) return;
      this.index = i;
      if (this.cur) this.cur.textContent = String(i + 1);
      if (this.prev) this.prev.disabled = i <= 0;
      if (this.next) this.next.disabled = i >= this.slides.length - 1;
      this.thumbs.forEach((t, k) => { if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
    }
  }
  customElements.define('soi-gallery', SoiGallery);


  /* ---------------------------------------------------------------- timeline chart follows the selected stage (05 C23) */
  document.addEventListener('soi:tab-change', (e) => {
    const host = e.target.closest('[data-soi-timeline]');
    if (!host) return;
    const chart = host.querySelector('.soi-tl__chart');
    if (chart) chart.dataset.stage = String(e.detail.index);
  });

  /* ---------------------------------------------------------------- science modal openers + buy-box focus links */
  const openScience = (id, tab, opener) => {
    const dialog = document.getElementById(id);
    if (!dialog) return;
    // the panels live in a <template> until the first open (DOM budget, R2-G-13)
    const tpl = dialog.querySelector('template[data-sci-tpl]');
    if (tpl) { tpl.replaceWith(tpl.content.cloneNode(true)); }
    const tabs = dialog.querySelector('soi-tabs');
    if (tabs && typeof tabs.select === 'function') tabs.select(Number(tab) || 0, false);
    if (window.soiUI) window.soiUI.openDialog(dialog, opener); else dialog.showModal();
  };
  document.addEventListener('click', (e) => {
    const o = e.target.closest('[data-soi-science-open]');
    if (o) { e.preventDefault(); openScience(o.dataset.soiScienceOpen, o.dataset.tab, o); return; }
    const back = e.target.closest('[data-science-back]');
    if (back) {
      const atc = document.querySelector('[data-soi="buybox"] [data-soi="atc"]');
      setTimeout(() => atc && atc.focus({ preventScroll: false }), 30);
      return;
    }
    const f = e.target.closest('[data-soi-focus-tier]');
    if (f) {
      const box = document.querySelector('soi-buy, soi-setbox');
      if (!box) return;
      e.preventDefault();
      const tier = Number(f.dataset.soiFocusTier) || 2;
      const anchor = document.getElementById('ritual') || box;
      anchor.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'start' });
      if (box.setTier) box.setTier(tier, true, true);
      else { const b = box.querySelector('[data-soi="atc"]'); b && b.focus({ preventScroll: true }); }
    }
  });
  /* compact first view (02 §3.5): the science link sits under the right column's last check; when that check wraps
     onto the link, the link falls back to its own row */
  const fitChecks = () => document.querySelectorAll('.soi-checks').forEach((box) => {
    box.classList.remove('soi-checks--flow');
    const link = box.querySelector('.soi-checks__link');
    if (!link || getComputedStyle(link).position !== 'absolute') return;
    const lr = link.getBoundingClientRect();
    const hit = [...box.querySelectorAll('.soi-checks__item > span')].some((t) => {
      const r = t.getBoundingClientRect();
      return !(r.right <= lr.left || lr.right <= r.left || r.bottom <= lr.top || lr.bottom <= r.top);
    });
    if (hit) box.classList.add('soi-checks--flow');
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fitChecks); else fitChecks();
  let fitRaf = 0;
  window.addEventListener('resize', () => { cancelAnimationFrame(fitRaf); fitRaf = requestAnimationFrame(fitChecks); }, { passive: true });
  const deepLink = () => {
    if (window.location.hash === '#inhaltsstoffe') {
      const d = document.querySelector('[data-soi-science]');
      if (d) openScience(d.id, 0, null);
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', deepLink); else deepLink();
})();
